import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { User } from '@stytch/vanilla-js';
import { Contract } from '@thera-hr/api-sdk/api/resources/db';
import { Employee } from '@thera-hr/check-api-sdk/api/types';
import posthog from 'posthog-js';

import { hasUsedEmailUpdateModal_LS_key } from 'components/app-components/account-preferences/general-tab/ChangeEmailModal';
import {
  initialTheraRoute_LS_key,
  initialTheraRouteTimestamp_LS_key,
} from 'components/container-components/AuthRoutingHandler';
import { _IUser, IRoleType, ISubRoleType } from 'context/user/user.type';
import { EmployeeType, EmployeeTypeToSubRoleType } from 'lib/constants';
import PosthogEvent from 'lib/posthogEvents';
import {
  EMAIL_DOMAINS_TO_SKIP_VERIFICATION,
  GAURAVS_PROD_COMPANYID,
  PROD_DEMO_ACC_COMPANYID,
  THERA_PROD_COMPANY_ID,
} from 'lib/whitelistConstants';
import { hasSentFirstVerifyEmail_LS_key } from 'pages/verify-email';
import { AdminRoutes, AuthRoutes, ContractorRoutes } from 'routes';
import { getCheckEmployee } from 'services/checkApi';
import { IAddress, ICompany, ICompanyAffiliation, IEmployment, IUser } from 'types';
import {
  getAccountSwitchingOptions,
  LS_key_Current_loggedIn_as_RoleType,
  LS_key_Current_loggedIn_as_SubRoleType,
  LS_key_Current_loggedIn_companyId,
} from './affiliations';
import { isBridgeCountrySupported, unsupportedUSAStates } from './country';
import { logError } from './error';
import { checkIfUserHasAccessToCompanyAndRole } from './rolesPermissions';
import { encodeEmail } from './string';
import { getTheraContract } from './transformation';
import { getCookie } from './cookie';
import { canRunPosthog } from './window';

export const THERA_SkipEmailVerify = 'thera__skipVerifyEmail';
export const THERA_SuperAdmin = 'isThera__superAdmin';
export const THERA_SupportAdmin = 'isThera__supportAdmin';
export const THERA_Skip2FA = 'thera__skip2FA';
// next-themes' default storage key. Theme is a device preference, so it must
// survive the localStorage wipe on logout (see UserProvider.logout).
export const THEME_STORAGE_KEY = 'theme';
export const dummyEmail = `dummyEmailForSU@thera.com`;

export const getEmployeeName = (
  theraUser?: Pick<IUser, 'firstName' | 'lastName'> | null,
  checkEmployee?: Pick<Employee, 'firstName' | 'lastName'> | null,
) => {
  const nameSource = theraUser || checkEmployee;
  const firstName = nameSource?.firstName || '';
  const lastName = nameSource?.lastName || '';

  return {
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`.trim(),
  };
};

export const isStytchProdEnv = process.env.NEXT_PUBLIC_STYTCH_PROJECT_ENV === 'test' ? false : true;

// Function to check if company is allowed to access Copilot demo
export const isTheraDemoCompany = (companyId: string): boolean => {
  if (!isStytchProdEnv) {
    // In non-prod environments, allow all companies
    return true;
  }

  // In production, only allow the specific demo company
  return companyId === PROD_DEMO_ACC_COMPANYID || companyId === THERA_PROD_COMPANY_ID;
};

// Re-export feature gating helpers from a lightweight module (safe to import in client bundles)
export { isHumanInterestIntegrationEnabled } from './featureGates';

/**
 * Feature flag for the paper-check Mail / Download via Checkeeper flow.
 *
 * Currently a closed pilot — visible in prod only on Thera's own and demo
 * companies, plus to super / support admins regardless of company. In non-prod
 * envs, always on.
 *
 * Mirrors the `isContractorProEnabled` shape so callers can drop it into the
 * same `isHidden` slots.
 */
export const isPaperCheckMailingEnabled = (companyId: string | undefined): boolean => {
  if (!companyId) return false;
  if (!isStytchProdEnv) return true;
  // Re-read localStorage at call time — the module-load `isSuperAdminMode`
  // snapshot can be stale on hydration / after a fresh impersonation.
  if (checkIfSuperAdminMode() || checkIfSupportAdminMode()) return true;
  return companyId === THERA_PROD_COMPANY_ID || companyId === PROD_DEMO_ACC_COMPANYID;
};

// Function to check if Contractor Pro feature should be enabled for a company
export const isContractorProEnabled = (companyId: string | undefined): boolean => {
  if (!companyId) {
    return false;
  }

  if (!isStytchProdEnv) {
    // In non-prod environments, allow all companies
    return true;
  }

  // In production, only allow Thera Prod, Demo, and Gaurav Prod
  return (
    companyId === THERA_PROD_COMPANY_ID || companyId === PROD_DEMO_ACC_COMPANYID || companyId === GAURAVS_PROD_COMPANYID
  );
};
export const isAdvancedSuperAdminMode =
  process.env.NEXT_PUBLIC_THERA_IS_ADVANCED_SUPER_ADMIN_MODE === 'true' ? true : false;

// Reads admin_type from the Stytch session JWT cookie without signature verification.
// Used only for FE gating - the backend is the authoritative security boundary.
// Stytch puts session custom claims at the root of the JWT payload, not inside its
// own `https://stytch.com/session` namespace, which carries only the session's own
// fields. Reading the namespaced path silently returned undefined for every admin.
export const getSessionAdminType = (): 'super' | 'support' | null => {
  try {
    const jwt = getCookie('stytch_session_jwt');
    if (!jwt) return null;
    const parts = jwt.split('.');
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const adminType = payload?.admin_type;
    return adminType === 'super' || adminType === 'support' ? adminType : null;
  } catch {
    return null;
  }
};

/**
 * Checks if the user is in super admin mode.
 * The session claim (attached by the backend after verifying an admin grant) is the
 * only source in production. localStorage is honored in non-prod Stytch environments
 * only, as a convenience for local development and e2e runs.
 */
export const checkIfSuperAdminMode = () => {
  const sessionAdminType = getSessionAdminType();
  if (sessionAdminType !== null) return sessionAdminType === 'super';
  if (!isStytchProdEnv && typeof window !== 'undefined' && window.localStorage) {
    return window.localStorage.getItem(THERA_SuperAdmin) === 'true';
  }
  return false;
};
export const isSuperAdminMode = checkIfSuperAdminMode();

/**
 * Checks if the user is in support admin mode.
 * Same sourcing rules as checkIfSuperAdminMode.
 */
export const checkIfSupportAdminMode = () => {
  const sessionAdminType = getSessionAdminType();
  if (sessionAdminType !== null) return sessionAdminType === 'support';
  if (!isStytchProdEnv && typeof window !== 'undefined' && window.localStorage) {
    return window.localStorage.getItem(THERA_SupportAdmin) === 'true';
  }
  return false;
};
export const isSupportAdminMode = checkIfSupportAdminMode();

/**
 * Checks if the super admin mode is on and as well as logged in
 * then only we will use this to surpass Maintenance Mode
 * @returns boolean
 * */
export const isSuperAdminReady = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    const isSuperAdminReady = window.localStorage.getItem(THERA_SkipEmailVerify);
    return Boolean(isSuperAdminReady);
  }
  return false;
};

// Skip email verification for these domains
const emailDomainsToSkipVerification = EMAIL_DOMAINS_TO_SKIP_VERIFICATION;

/**
 * Checks whether the user's own email is verified on Stytch.
 * Admin sessions never reach this: UserProvider decides the bypass before calling.
 * @param stytchUser
 * @returns boolean
 */
export const checkIfEmailVerified = async (stytchUser: User) => {
  // An empty emails array is still truthy, so index before reading: a Stytch user with no
  // email on record used to throw here rather than fall through to the guard below.
  const email = stytchUser?.emails?.[0]?.email ?? '';
  if (!stytchUser || !email) {
    return false;
  }
  /**
   * Temporary fix for Proxima's user email verification issue
   */
  const emailDomain = email?.split('@')[1];
  if (emailDomainsToSkipVerification.includes(emailDomain)) {
    console.warn('Skipping email verification for ', email);
    return true;
  }

  return stytchUser.emails?.[0]?.verified ?? false;
};

export const checkIfNewInvitedUserWithoutPasswordAndNoOAuth = (stytchUser: User) => {
  if (stytchUser && !stytchUser.password && !stytchUser?.providers?.length) {
    return true;
  }
  return false;
};

export const groupContractsByCompanyId = (
  contractsWithCompany: {
    contract: IEmployment;
    company: ICompany;
  }[],
): {
  [companyId: string]: { contracts: IEmployment[]; companyName: string; teams?: any };
} => {
  const groupedContracts = contractsWithCompany.reduce((acc, contractAndCompany) => {
    const { company, contract } = contractAndCompany;
    const { companyID, companyName = '' } = company || {};
    if (!acc[companyID]) {
      acc[companyID] = { contracts: [], companyName };
    }
    acc[companyID].contracts.push(contract);
    return acc;
  }, {});
  return groupedContracts;
};

/**
 * Checks if the ID should be shown to copy
 * @param dependencies - The dependencies to check
 * @param forcedOverride - Whether to force override the check (last parameter, default is false)
 * @returns boolean
 */
export const showIDToCopy = (...dependencies: any[]) => {
  // Extract forcedOverride from the last parameter if it's a boolean
  const lastParam = dependencies[dependencies.length - 1];
  const forcedOverride = typeof lastParam === 'boolean' ? lastParam : false;
  const actualDependencies = typeof lastParam === 'boolean' ? dependencies.slice(0, -1) : dependencies;

  // return false if any of the arguments is null or undefined
  if (actualDependencies.some((dependency) => !dependency)) {
    return false;
  }
  // return true if forced override is true
  if (forcedOverride) {
    return true;
  }
  if (isSuperAdminMode || isSupportAdminMode) {
    return true;
  }
  return false;
};

export const doWorkerActions = async ({ user, contract }: { user: _IUser; contract: IEmployment }) => {
  let updatedUser = user;
  let routeToRedirect = '';
  const employmentType = contract?.type;
  // set the userEmploymentType, contract and company in context if user is employee to avoid extra API calls
  const isEOREmployee = employmentType === EmployeeType.EMPLOYEE;
  const isCheckEmployee = Boolean(contract?.w2Payroll?.checkEmployeeId);
  const isWorkerOnboarded = user?.employeeInformation?.address !== null;
  const userEmploymentType = isEOREmployee
    ? EmployeeType.EMPLOYEE
    : isCheckEmployee
      ? EmployeeType.US_EMPLOYEE
      : EmployeeType.CONTRACTOR;

  updatedUser = {
    ...user,
    isOnboarded: isWorkerOnboarded,
    userEmploymentType,
  };

  // if the user is check employee, redirect to check dashboard
  if (isCheckEmployee) {
    const theraCompanyID = contract?.companyID;
    const checkEmployeeID = contract?.w2Payroll?.checkEmployeeId;
    const checkEmployee: Employee = await getCheckEmployee({ employeeID: checkEmployeeID, theraCompanyID })
      .then((response) => response)
      .catch((error) => {
        logError(error);
      });
    /**
     * Determine if Check employee should be considered "onboarded" and allowed to access dashboard.
     *
     * An employee is considered onboarded if:
     * 1. Their onboard status is 'completed' (fully done), OR
     * 2. Their status is 'blocking' BUT the only blocking step is 'company_defined_attributes'
     *
     * Why we allow 'company_defined_attributes' as the only blocking step:
     * - Company-defined attributes (like jurisdiction/tax settings) are administrative
     *   and don't prevent the employee from using the core platform functionality
     * - Other blocking steps like 'employee_details', 'ssn', 'payment_method' are critical
     *   for payroll and must be completed before dashboard access
     * - This allows employees to start using the platform while admin completes
     *   the final administrative setup steps
     */
    const isOnboarded = isCheckEmployeeOnboarded(checkEmployee);
    updatedUser = {
      ...updatedUser,
      isOnboarded,
    };

    // if the check employee is not onboarded, redirect to onboarding
    if (!isOnboarded) {
      routeToRedirect = `/employee-onboarding?employeeID=${checkEmployeeID}`;
    }
  }

  if (!isWorkerOnboarded) {
    routeToRedirect = AuthRoutes.SignUpWorkerOnboarding;
  }

  return { updatedUser, routeToRedirect };
};

export const isValidRoute = (route: string, roleType: 'manager' | 'worker' = 'worker') => {
  if (roleType === 'manager') {
    return Object.values(AdminRoutes).some((adminRoute) => adminRoute === route);
  } else if (roleType === 'worker') {
    return Object.values(ContractorRoutes).some((workerRoute) => workerRoute === route);
  }
  return false;
};

/**
 * transform the affiliations to populate the contract object
 * */
export const getTransformedAffiliations = (affiliationsByCompany: Record<string, ICompanyAffiliation>) => {
  const transformAffiliations = Object.keys(affiliationsByCompany).reduce((acc, companyId) => {
    const companyAffiliation = affiliationsByCompany[companyId];
    const transformedContracts = companyAffiliation.contracts.map((contract) => ({
      ...getTheraContract(contract as unknown as IEmployment),
    }));
    const transformedW2Contracts = companyAffiliation.w2Contracts.map((contract) => ({
      ...getTheraContract(contract as unknown as IEmployment),
    }));
    const transformedContractorContracts = companyAffiliation.contractorContracts.map((contract) => ({
      ...getTheraContract(contract as unknown as IEmployment),
    }));
    const transformedEORContracts = companyAffiliation.eorContracts.map((contract) => ({
      ...getTheraContract(contract as unknown as IEmployment),
    }));
    acc[companyId] = {
      ...companyAffiliation,
      contracts: transformedContracts,
      w2Contracts: transformedW2Contracts,
      contractorContracts: transformedContractorContracts,
      eorContracts: transformedEORContracts,
    };

    return acc;
  }, {});
  return transformAffiliations;
};

/**
 * Clear Local Storage
 */
export const clearLocalStorage = () => {
  localStorage.removeItem(initialTheraRoute_LS_key);
  localStorage.removeItem(initialTheraRouteTimestamp_LS_key);
  localStorage.removeItem(LS_key_Current_loggedIn_as_RoleType);
  localStorage.removeItem(LS_key_Current_loggedIn_as_SubRoleType);
  localStorage.removeItem(LS_key_Current_loggedIn_companyId);
};

export const clearLSEmailProps = (email = '') => {
  localStorage.removeItem(hasSentFirstVerifyEmail_LS_key);

  // remove hasUsedEmailUpdateModal_LS_key only when the stytchUser contains the correct verified email
  if (localStorage.getItem(hasUsedEmailUpdateModal_LS_key) === email) {
    localStorage.removeItem(hasUsedEmailUpdateModal_LS_key);
  }
};

export const getUserFirstContract = ({
  userSubRoleType,
  userSavedCompanyId,
  affiliationsByCompany,
}: {
  userSubRoleType: 'w2Employee' | 'contractor' | 'eorEmployee';
  userSavedCompanyId: string;
  affiliationsByCompany: Record<string, ICompanyAffiliation>;
}) => {
  let firstContract: Contract;
  if (userSubRoleType === 'w2Employee') {
    firstContract = affiliationsByCompany[userSavedCompanyId]?.w2Contracts?.[0];
  } else if (userSubRoleType === 'contractor') {
    firstContract = affiliationsByCompany[userSavedCompanyId]?.contractorContracts?.[0];
  } else if (userSubRoleType === 'eorEmployee') {
    firstContract = affiliationsByCompany[userSavedCompanyId]?.eorContracts?.[0];
  } else {
    firstContract = affiliationsByCompany[userSavedCompanyId]?.contracts?.[0];
  }
  return firstContract;
};

/**
 * Set the current user to PostHog and Datadog.
 * if not provided, it resets to empty/null
 */
export const setPostHogAndDatadogUser = ({
  userId = '',
  email = '',
  name = '',
  userRoleType = '',
  userSubRoleType = '',
}: { userId?: string; email?: string; name?: string; userRoleType?: string; userSubRoleType?: string } = {}) => {
  if (userId && email) {
    if (canRunPosthog) {
      posthog.identify(userId, {
        email,
      });
    }
    datadogLogs.setUser({ id: userId, email, name });
    datadogRum.setUser({ id: userId, email, name });
    datadogLogs.setUserProperty('userRoleType', userRoleType);
    datadogLogs.setUserProperty('userSubRoleType', userSubRoleType);
    datadogRum.setUserProperty('userRoleType', userRoleType);
    datadogRum.setUserProperty('userSubRoleType', userSubRoleType);
  } else {
    if (canRunPosthog) posthog.reset();
    datadogLogs.clearUser();
    datadogLogs.removeUserProperty('userRoleType');
    datadogRum.clearUser();
    datadogRum.removeUserProperty('userRoleType');
  }
};

/**
 * if stytch user exists but user profile on Thera does not
 */
export const getStytch404RedirectRoute = ({
  email,
  canCreateAccount = false,
}: {
  email: string;
  canCreateAccount: boolean;
}) => {
  // The book-a-meeting redirect is Thera-branded and only appropriate at app.getthera.com.
  // Any other host in stytch-prod is a white-label deployment, and WLCs gate onboarding
  // via their own intake — let those users continue into onboarding.
  const isOnTheraProdHost = typeof window !== 'undefined' && window.location.hostname === 'app.getthera.com';

  if (!isStytchProdEnv || checkIfSuperAdminMode() || !isOnTheraProdHost) {
    return `${AuthRoutes.SignUpOnboarding}`;
  }
  if (!canCreateAccount) {
    PosthogEvent.captureBookAMeeting({ email });
    return `${AuthRoutes.SignUpNeedsBooking}`;
  }
  return `${AuthRoutes.SignUpOnboarding}`;
};

export const routeToRedirectToEmailVerification = (email: string, routerPathName: string) => {
  if (checkIfSuperAdminMode()) return;
  if (routerPathName === AuthRoutes.SuperAdmin) return;
  const routeToRedirect = `${AuthRoutes.EmailVerification}?email=${encodeEmail(email)}`;
  return routeToRedirect;
};

export const routeToRedirectToSetPassword = (routerPathName: string) => {
  if (checkIfSuperAdminMode()) return;
  if (routerPathName === AuthRoutes.SuperAdmin) return;
  const routeToRedirect = `${AuthRoutes.ResetPassword}?create_new_password=true`;
  return routeToRedirect;
};

export const getUserContextData = async ({
  newCompanyId,
  affiliationsByCompany,
  user,
  userSubRoleType,
  userRoleType,
  userSavedCompanyId,
}: {
  userRoleType: IRoleType;
  userSubRoleType: ISubRoleType;
  user: _IUser;
  affiliationsByCompany: Record<string, ICompanyAffiliation>;
  newCompanyId: string;
  userSavedCompanyId: string;
}): Promise<{
  userCurrentRoleType: IRoleType;
  userCurrentSubRoleType: ISubRoleType;
  redirectRoute: string;
  userCurrentCompany: ICompany | undefined;
  userCurrentContract: Contract | undefined;
  canShowAccountSwitcher: boolean;
  updatedUser: _IUser;
}> => {
  const numberOfCompanies = Object.keys(affiliationsByCompany).length;
  let userCurrentCompanyId = userSavedCompanyId;
  let userCurrentRoleType = userRoleType;
  let userCurrentSubRoleType = userSubRoleType;
  let routeToRedirect = undefined;
  let userCurrentCompany: ICompany | undefined;
  let userEmploymentType: EmployeeType | undefined;
  let userCurrentContract: Contract | undefined;
  let canShowAccountSwitcher = false;
  let updatedUser = user;

  // if another company being added,
  if (newCompanyId) {
    userCurrentCompanyId = newCompanyId;
    const saved_company = affiliationsByCompany[userCurrentCompanyId]?.company;
    userCurrentRoleType = 'manager';
    userCurrentCompany = saved_company as unknown as ICompany;
    // persist the new values to Local Storage
    window.localStorage.setItem(LS_key_Current_loggedIn_companyId, userCurrentCompanyId);
    window.localStorage.setItem(LS_key_Current_loggedIn_as_RoleType, 'manager');

    routeToRedirect = `${AdminRoutes.Dashboard}`;
  } else {
    // if role type is saved but company id is not saved, load the first company
    if (userCurrentRoleType && !userCurrentCompanyId) {
      userCurrentCompanyId = Object.keys(affiliationsByCompany)[0];
      userCurrentCompany = affiliationsByCompany[userCurrentCompanyId]?.company as unknown as ICompany;
      window.localStorage.setItem(LS_key_Current_loggedIn_companyId, userCurrentCompanyId);
    }

    const isLoadedFromLocalStorage = checkIfUserHasAccessToCompanyAndRole(
      userCurrentCompanyId,
      userCurrentRoleType,
      affiliationsByCompany,
    );

    // load the saved company and role type from local storage
    if (isLoadedFromLocalStorage) {
      const saved_company = affiliationsByCompany[userCurrentCompanyId]?.company;
      userCurrentCompany = saved_company as unknown as ICompany;
      // if user has worker role, will load the first contract (below)
    } else {
      // if there are multiple companies, show switch account modal
      // else load the company if user has the manager role
      // or load the worker role
      if (numberOfCompanies > 1) {
        canShowAccountSwitcher = true;
        // go to home page and show switch account modal to select the company/account
        routeToRedirect = `${AdminRoutes.Dashboard}`;
      } else {
        // pick the companyId, here we will have only one company as multiple companies scenario is already discussed above
        const companyId = Object.keys(affiliationsByCompany)[0];
        userCurrentCompanyId = companyId;
        const affiliation = affiliationsByCompany[companyId];
        const accountSwitchOptions = getAccountSwitchingOptions(affiliation);
        // if the user has multiple roles in the company, show switch account modal
        if (accountSwitchOptions.length > 1) {
          // multiple roles in the company
          canShowAccountSwitcher = true;
          // go to home page and show switch account modal to select the company/account
          routeToRedirect = `${AdminRoutes.Dashboard}`;
        } else {
          // single role
          // if user has manager role, load the company, else load the worker role
          userCurrentCompany = affiliation?.company as unknown as ICompany;
          const managerRole = accountSwitchOptions.find((option) => option.type === 'manager');
          if (managerRole) {
            userCurrentRoleType = 'manager';
          } else {
            userCurrentRoleType = 'worker';
          }
        }
      }
    }

    if (userCurrentRoleType === 'worker') {
      const firstContract = getUserFirstContract({
        affiliationsByCompany,
        userSavedCompanyId: userCurrentCompanyId,
        userSubRoleType: userCurrentSubRoleType,
      });
      userEmploymentType = firstContract?.type as EmployeeType;
      userCurrentContract = firstContract;
      if (!userCurrentSubRoleType) {
        userCurrentSubRoleType = EmployeeTypeToSubRoleType[userEmploymentType] as ISubRoleType;
      }
      const { updatedUser: _user, routeToRedirect: route } = await doWorkerActions({
        user,
        contract: userCurrentContract as unknown as IEmployment,
      });

      updatedUser = {
        userEmploymentType,
        ..._user,
      };
      routeToRedirect = route;
    }
  }

  return {
    updatedUser,
    userCurrentRoleType,
    userCurrentSubRoleType,
    userCurrentCompany,
    userCurrentContract,
    redirectRoute: routeToRedirect,
    canShowAccountSwitcher,
  };
};

/**
 * Checks if a user can create a Bridge wallet
 * @param address - The user's address
 * @param userCompanyID - The ID of the user's company
 * @returns boolean
 */
export const canUserCreateBridgeWallet = (address?: IAddress) => {
  const isUSCompany = address?.country === 'US';
  const isUSStateSupported = unsupportedUSAStates.includes(address?.state);
  const isUserCountrySupported = isBridgeCountrySupported(address?.country);
  const isWalletCreationAllowed = (isUSCompany && !isUSStateSupported) || isUserCountrySupported;
  return isWalletCreationAllowed;
};

// Countries where Bridge cards are allowed
const bridgeCardAllowedCountries = ['AR', 'BO', 'BR', 'CO', 'DO', 'EC', 'GT', 'MX', 'PE', 'US', 'NG'];

/**
 * Checks if a user can create a Bridge card
 * @param address - The user's address
 * @returns boolean
 */
export const canUserCreateBridgeCard = (address?: IAddress) => {
  if (!address?.country) return false;

  const isCountryAllowed = bridgeCardAllowedCountries.includes(address.country);

  // For US users, also check the state restrictions
  if (address.country === 'US') {
    const isUSStateSupported = !unsupportedUSAStates.includes(address?.state);
    return isCountryAllowed && isUSStateSupported;
  }

  return isCountryAllowed;
};

export const getIsUSContractor = (employeeInformation: IUser['employeeInformation']): boolean => {
  const isEntity = employeeInformation?.type === 'entity';
  const isUSByAddress = isEntity
    ? employeeInformation?.entityInformation?.address?.country === 'US'
    : employeeInformation?.country === 'US';
  const usTaxpayerFlag = isEntity
    ? employeeInformation?.entityInformation?.usTaxpayer
    : employeeInformation?.usTaxpayer;
  const nonUsTaxpayerFlag = isEntity
    ? employeeInformation?.entityInformation?.nonUsTaxpayer
    : employeeInformation?.nonUsTaxpayer;
  return isUSByAddress ? nonUsTaxpayerFlag !== true : usTaxpayerFlag === true;
};

export const getIsUSContractorByAddress = (employeeInformation: IUser['employeeInformation']): boolean => {
  const isEntity = employeeInformation?.type === 'entity';
  return isEntity
    ? employeeInformation?.entityInformation?.address?.country === 'US'
    : employeeInformation?.country === 'US';
};

export const isCheckEmployeeOnboarded = (checkEmployee: Employee) => {
  const onboard = checkEmployee?.onboard;
  if (!onboard) return false;

  // The Fern SDK returns camelCase; the Thera backend proxy returns snake_case.
  // Support both so this gate works regardless of which data source calls it.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onboardAny = onboard as any;
  const blockingSteps: string[] | undefined = onboard.blockingSteps ?? onboardAny.blocking_steps;
  const remainingSteps: string[] | undefined = onboard.remainingSteps ?? onboardAny.remaining_steps;
  const companyDefinedAttributes: unknown[] | undefined =
    onboard.companyDefinedAttributes ?? onboardAny.company_defined_attributes;

  return (
    onboard.status === 'completed' ||
    (onboard.status === 'blocking' &&
      blockingSteps?.length === 1 &&
      blockingSteps?.[0] === 'company_defined_attributes' &&
      (companyDefinedAttributes?.length ?? 0) > 0) ||
    (onboard.status === 'needs_attention' &&
      remainingSteps?.length === 1 &&
      remainingSteps?.[0] === 'company_defined_attributes' &&
      (companyDefinedAttributes?.length ?? 0) > 0)
  );
};
